import React, { useMemo, useEffect } from "react";
import { Controller } from "react-hook-form";
import { Switch } from "@mui/material";
import Input from "components/Input";
import * as Styled from "./styles";
import { useProviderFormContext } from "../../Context/ValidationContext";
import { ProviderCode } from "../../../../../../enums";
import { getProviderCapabilities } from "../../utils/providerHelpers";

interface BasicInfosProps {
  onStatusChange?: (isActive: boolean) => void;
  isEditMode?: boolean;
}

const BasicInfos = ({
  onStatusChange,
  isEditMode = false,
}: BasicInfosProps) => {
  const {
    providers,
    priorityOptions,
    register,
    watch,
    setValue,
    control,
    formErrors: errors,
    internalReadOnly: readOnly,
    selectedProvider,
    clearErrors,
  } = useProviderFormContext();

  const capabilities = useMemo(
    () => getProviderCapabilities(selectedProvider),
    [selectedProvider],
  );

  const showMcc = !selectedProvider || capabilities.showMcc;
  const showMerchantId = !selectedProvider || capabilities.hasMerchantId;

  const isActive = watch("isActive");

  useEffect(() => {
    if (!capabilities.isPriorityEditable) {
      setValue("priority", capabilities.priorityFixedValue, {
        shouldValidate: false,
      });
      clearErrors("priority");
    }
  }, [
    capabilities.isPriorityEditable,
    capabilities.priorityFixedValue,
    setValue,
    clearErrors,
  ]);

  const handleActivateProvider = async () => {
    const newValue = !isActive;
    setValue("isActive", newValue);

    if (isEditMode && onStatusChange) {
      await onStatusChange(newValue);
    }
  };

  const filteredProviderOptions = useMemo(() => {
    return providers.filter(
      (provider) =>
        provider.code?.toLowerCase() !==
        ProviderCode.LocalPayment.toLowerCase(),
    );
  }, [providers]);

  return (
    <Styled.Container>
      <Styled.ImportantNotice>
        <Styled.InfoIconStyled />
        <Styled.InfoTypography variant="body2">
          <Styled.ImportantLabel>Importante: </Styled.ImportantLabel>
          Caso não encontre os dados necessários para preenchimento das
          informações no portal do Provedor, entre em contato com o suporte
          desse Provedor.
        </Styled.InfoTypography>
      </Styled.ImportantNotice>

      <Styled.FormContainer>
        <Styled.LeftColumn>
          <Controller
            name="provider"
            control={control}
            rules={{ required: "Campo obrigatório" }}
            render={({ field }) => (
              <Input
                select
                name="provider"
                required
                label="Provedor"
                error={errors.provider}
                value={field.value || ""}
                onChange={field.onChange}
                onBlur={field.onBlur}
                selectOptions={
                  filteredProviderOptions.length > 0
                    ? filteredProviderOptions.map((option) => ({
                        value: option.id,
                        name: option.name,
                      }))
                    : undefined
                }
                fullWidth
                disabled={readOnly || filteredProviderOptions.length === 0}
              />
            )}
          />

          {capabilities.isPriorityEditable && (
            <Controller
              name="priority"
              control={control}
              rules={{
                required: capabilities.isPriorityEditable
                  ? "Campo obrigatório"
                  : false,
              }}
              render={({ field }) => {
                const handleChange = (
                  e: React.ChangeEvent<HTMLInputElement>,
                ) => {
                  const { value } = e.target;
                  field.onChange(value === "" ? undefined : Number(value));
                };

                return (
                  <Input
                    select
                    name="priority"
                    required
                    label="Prioridade de uso"
                    error={errors.priority}
                    value={field.value?.toString() || ""}
                    onChange={handleChange}
                    onBlur={field.onBlur}
                    selectOptions={
                      priorityOptions.length > 0
                        ? priorityOptions.map((option) => ({
                            value: option.toString(),
                            name: option.toString(),
                          }))
                        : [{ value: "", name: "Carregando..." }]
                    }
                    fullWidth
                    disabled={readOnly || priorityOptions.length === 0}
                  />
                );
              }}
            />
          )}
        </Styled.LeftColumn>

        <Styled.RightColumn>
          <Styled.StatusContainer>
            <Styled.StatusContent>
              <Styled.StatusTypography variant="body2">
                Qual o status deste provedor de pagamento?
              </Styled.StatusTypography>
              <Styled.StatusCaptionTypography variant="caption">
                Status inativo não entra na lista de prioridade
              </Styled.StatusCaptionTypography>
            </Styled.StatusContent>
            <Styled.SwitchContainer isActive={isActive}>
              <Styled.FormControlLabelStyled
                control={
                  <Switch
                    checked={isActive}
                    onChange={handleActivateProvider}
                    color="success"
                    size="small"
                    disabled={readOnly}
                  />
                }
                label={isActive ? "Ativo" : "Inativo"}
                labelPlacement="start"
              />
            </Styled.SwitchContainer>
          </Styled.StatusContainer>

          {showMcc && (
            <Input
              type="number"
              inputMode="numeric"
              disabled
              name="mcc"
              label="MCC*"
              error={errors.mcc}
              register={register}
              fullWidth
            />
          )}
        </Styled.RightColumn>

        {showMerchantId && (
          <Styled.FullWidthInputWrapper>
            <Controller
              name="merchantId"
              control={control}
              rules={{ required: showMerchantId ? "Campo obrigatório" : false }}
              render={({ field }) => (
                <Input
                  required={showMerchantId}
                  name="merchantId"
                  label="Merchant ID principal"
                  error={errors.merchantId}
                  value={field.value || ""}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  fullWidth
                  placeholder="ID do estabelecimento no Adquirente"
                  disabled={readOnly}
                />
              )}
            />
          </Styled.FullWidthInputWrapper>
        )}
      </Styled.FormContainer>
    </Styled.Container>
  );
};

export default BasicInfos;
